import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Wand2, Database } from 'lucide-react'
import { AiSparkle } from '@/components/AiSparkle'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { authHeaders } from '@/features/ai-chat/core/utils/authHeaders'
import { PhaseSteps, appendPhase } from '@/components/ui/phase-steps'
import { DotSphereSpinner } from '@/components/ui/DotSphereSpinner'
import type { RunbookTask } from '@/types/entities'

interface GenerationTarget {
    masterId: number | null
    masterDescription: string | null
    objectDescription: string | null
    generatedName: string | null
    technologies: { category: string; name: string }[]
    generationContext: string
    versionCount: number
    chain?: Array<{ kind: string; chainSet: string; count: number }>
}

interface PlaceholderPreview {
    filled: Array<{ name: string; value: string; usedInRunbook: boolean }>
    atRecovery: string[]
    credentials: string[]
    hasMetadata: boolean
}

interface ObjectRunbookAiDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    objectId: string
    scenario: number | null
    /** Called with the regenerated tasks + scripts so the page can save and refresh. */
    onRegenerated: (tasks: RunbookTask[], code: Record<string, unknown>) => Promise<void> | void
}

/**
 * The two AI options on an object's runbook.
 *
 * There are three tiers, and they are not the same thing:
 *
 *   A MASTER runbook is generic. The one for Active Directory has to fit every Active
 *   Directory anyone will ever recover, so it is written with placeholders.
 *
 *   An OBJECT runbook belongs to one object in one customer's estate. Its tasks are already
 *   specific to that customer — their layers, their dependencies, their chain.
 *
 *   Filling the placeholders is what makes a runbook specific to a SERVER: the hostname,
 *   the addresses, the operating system, the drives.
 *
 * This screen does ONE of those and shows the third. Regenerate rewrites the tasks knowing what
 * this object actually is and what is actually held for it. Filling the placeholders is NOT
 * offered here: it belongs to a recovery runbook, where the values are the ones the recovery
 * actually used and the substitution is a record of what happened. Doing it on the object
 * runbook would bake one server's values into the thing that describes the object.
 *
 * So the placeholders are shown, not applied — the useful question here is whether this
 * runbook knows enough about this object to be worth anything.
 *
 * Credentials are never filled anywhere. They are supplied by the engineer at the time and
 * are deliberately not stored.
 */
export function ObjectRunbookAiDialog({
    open,
    onOpenChange,
    objectId,
    scenario,
    onRegenerated,
}: ObjectRunbookAiDialogProps) {
    const { t } = useTranslation()
    const [target, setTarget] = useState<GenerationTarget | null>(null)
    const [loadingTarget, setLoadingTarget] = useState(false)
    const [busy, setBusy] = useState<'regenerate' | null>(null)
    const [phases, setPhases] = useState<string[]>([])
    const [error, setError] = useState<string | null>(null)
    const [placeholders, setPlaceholders] = useState<PlaceholderPreview | null>(null)
    const [rationale, setRationale] = useState<string | null>(null)
    const [failure, setFailure] = useState<any>(null)
    const [toolsUsed, setToolsUsed] = useState<string[]>([])

    useEffect(() => {
        if (!open) {
            setPhases([])
            setError(null)
            setPlaceholders(null)
            setRationale(null)
            setToolsUsed([])
            setFailure(null)
            setBusy(null)
            return
        }
        setLoadingTarget(true)
        fetch(`/api/objects/${encodeURIComponent(objectId)}/runbook/generation-target`, {
            headers: authHeaders(),
        })
            .then((r) => r.json())
            .then((body) => setTarget(body?.data ?? body ?? null))
            .catch(() => setTarget(null))
            .finally(() => setLoadingTarget(false))

        fetch(`/api/objects/${encodeURIComponent(objectId)}/placeholders${scenario != null ? `?scenario=${scenario}` : ''}`, {
            headers: authHeaders(),
        })
            .then((r) => r.json())
            .then((body) => setPlaceholders(body?.data ?? body ?? null))
            .catch(() => setPlaceholders({ filled: [], atRecovery: [], credentials: [], hasMetadata: false }))
    }, [open, objectId, scenario])

    const hasVaultData = (target?.versionCount ?? 0) > 0

    const technologiesLabel = useMemo(
        () => (target?.technologies || []).map((x) => x.name).join(', '),
        [target]
    )

    /**
     * Regeneration runs the AUTHORING AGENT, not the one-shot pipeline.
     *
     * The agent looks things up before it writes: what this object actually is, what is held
     * for it in the vault and in what order, whether a runbook for this technology already
     * exists to follow, and what is installed on the machine that will run the commands. It
     * then checks its own commands against that host and submits a validated draft rather
     * than prose someone has to parse.
     *
     * Nothing is saved until you accept it — the agent persists nothing itself.
     */
    const handleRegenerate = async () => {
        if (!target) return
        setBusy('regenerate')
        setError(null)
        setFailure(null)
        setPhases(['Starting...'])
        try {
            const response = await fetch('/api/ai/runbook-author/author', {
                method: 'POST',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({
                    objectId,
                    // Where the commands will be run. The jump server is the host whose image
                    // we ship, so its toolchain is a guarantee rather than a hope.
                    hostKey: 'jump-linux',
                    guidance: target.generationContext || '',
                }),
            })
            if (!response.ok || !response.body) {
                throw new Error(`Generation failed (${response.status})`)
            }

            const reader = response.body.getReader()
            const decoder = new TextDecoder()
            let buffer = ''
            let tasks: RunbookTask[] | null = null
            let code: Record<string, unknown> = {}

            for (;;) {
                const { done, value } = await reader.read()
                if (done) break
                buffer += decoder.decode(value, { stream: true })
                const chunks = buffer.split('\n\n')
                buffer = chunks.pop() || ''
                for (const chunk of chunks) {
                    const eventLine = chunk.split('\n').find((l) => l.startsWith('event: '))
                    const dataLine = chunk.split('\n').find((l) => l.startsWith('data: '))
                    if (!eventLine || !dataLine) continue
                    const event = eventLine.slice(7).trim()
                    let payload: any = {}
                    try {
                        payload = JSON.parse(dataLine.slice(6))
                    } catch {
                        continue
                    }
                    if (event === 'status' && payload.status) {
                        setPhases((prev) => appendPhase(prev, payload.status))
                    }
                    if (event === 'error') {
                        // Keep everything the route sent — which violation, what it said
                        // instead, which tools it used — rather than only the headline.
                        setFailure(payload)
                        throw new Error(payload.error || 'The agent did not produce a runbook')
                    }
                    if (event === 'draft') {
                        tasks = (payload.tasks || []).map((task: any, i: number) => ({
                            id: null,
                            taskNo: task.taskNo ?? i + 1,
                            task: task.task || '',
                            commands: task.commands || '',
                            duration: task.duration ?? 0,
                            taskOffset: null,
                            startTime: null,
                            finishTime: null,
                            dependencies: task.dependencies || '',
                            mode: task.mode || 'manual',
                            method: task.method || 'manual',
                            optionGroup: task.optionGroup ?? null,
                            optionName: task.optionName ?? null,
                            optionDefault: task.optionDefault ?? false,
                        }))
                        // What it looked at and why, so the author can judge whether to trust it
                        // rather than being handed tasks with no provenance.
                        setRationale(payload.rationale || null)
                        setToolsUsed(payload.toolsUsed || [])
                    }
                }
            }

            if (!tasks || tasks.length === 0) throw new Error('The generator returned no tasks')
            setPhases((prev) => appendPhase(prev, `Writing ${tasks.length} tasks to the runbook...`))
            await onRegenerated(tasks, code)
            onOpenChange(false)
        } catch (e: any) {
            setError(e?.message || 'Generation failed')
        } finally {
            setBusy(null)
        }
    }


    return (
        <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <AiSparkle size={20} className="text-primary" />
                        {t('objects.runbook.aiOptions', 'Improve this runbook with Clara')}
                    </DialogTitle>
                    <DialogDescription>
                        {t(
                            'objects.runbook.aiOptionsDescription',
                            'A master runbook is written generically, to fit every object of its type. This one belongs to this object, so its tasks are already specific to your estate. Regenerating rewrites them from what the object actually is; filling in the placeholders is what makes them specific to this server.'
                        )}
                    </DialogDescription>
                </DialogHeader>

                {loadingTarget ? (
                    <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {t('common.loading', 'Loading...')}
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* What we actually hold — this is what decides whether regenerating is worth it */}
                        <div className="rounded-md border bg-muted/40 p-3 text-sm space-y-1">
                            <div className="flex items-center gap-2">
                                <Database className="h-4 w-4 text-muted-foreground" />
                                {hasVaultData ? (
                                    <span>
                                        {/* Recovery_Chain, not a count of everything in the vault:
                                            these are the versions a recovery would actually
                                            apply. Naming them tells an engineer what kind of
                                            night it is — Boot + Full is not the same work as a
                                            Full with six incrementals behind it. */}
                                        {t('objects.runbook.aiVaultData', 'Recovery chain:')}{' '}
                                        {(target?.chain || []).map((c, i) => (
                                            <span key={`${c.kind}-${c.chainSet}-${i}`} className="ml-1">
                                                <Badge variant="secondary">
                                                    {c.kind}{c.count > 1 ? ` ×${c.count}` : ''}
                                                    {c.chainSet === 'Secondary' ? ' (secondary)' : ''}
                                                </Badge>
                                            </span>
                                        ))}
                                    </span>
                                ) : (
                                    <span className="text-amber-700 dark:text-amber-400">
                                        {t(
                                            'objects.runbook.aiNoVaultData',
                                            'Nothing is held in the vault for this object yet, so a regenerated runbook would still be generic.'
                                        )}
                                    </span>
                                )}
                            </div>
                            {technologiesLabel && (
                                <div className="text-xs text-muted-foreground">
                                    {t('objects.runbook.aiStack', 'Stack')}: {technologiesLabel}
                                </div>
                            )}
                        </div>

                        {/* Option 1 — regenerate against real content */}
                        <div className="rounded-md border p-4 space-y-2">
                            <div className="flex items-center gap-2 font-medium">
                                <AiSparkle size={16} className="text-primary" />
                                {t('objects.runbook.aiRegenerate', 'Regenerate from this object')}
                            </div>
                            {rationale && (
                                <div className="rounded-md bg-muted/50 p-3 text-xs space-y-2">
                                    <div className="text-foreground">{rationale}</div>
                                    {toolsUsed.length > 0 && (
                                        <div className="text-muted-foreground">
                                            {t('objects.runbook.aiLookedAt', 'It looked at')}: {toolsUsed.join(', ')}
                                        </div>
                                    )}
                                </div>
                            )}
                            <p className="text-sm text-muted-foreground">
                                {t(
                                    'objects.runbook.aiRegenerateHelp',
                                    'Runs the authoring agent. It looks up what this object is, what is held for it in the vault and in what order, whether a runbook for this technology already exists to follow, and what is installed on the machine that will run the commands — then checks its own commands against that host before submitting. This replaces the current runbook, so use it on one you have not customised.'
                                )}
                            </p>
                            <Button
                                onClick={handleRegenerate}
                                disabled={!!busy || !target}
                                className="bg-purple-600 hover:bg-purple-700 text-white"
                                size="sm"
                            >
                                {busy === 'regenerate' ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        {t('common.working', 'Working...')}
                                    </>
                                ) : (
                                    <>
                                        <AiSparkle size={16} className="mr-2" />
                                        {t('objects.runbook.aiRegenerateAction', 'Regenerate runbook')}
                                    </>
                                )}
                            </Button>

                            {/* What it is actually doing. Every tool call the agent makes is
                                narrated by the route, so this is the difference between a job
                                you can watch and a spinner you have to trust. */}
                            {busy === 'regenerate' && phases.length > 0 && (
                                <div className="mt-3 rounded-lg border bg-muted/30 p-4">
                                    <div className="flex items-start gap-4">
                                        {/* Out of layout flow over a fixed slot, so the mark is drawn
                                            large without the panel growing around it — the same
                                            arrangement as master runbook generation. */}
                                        <div className="relative h-10 w-16 flex-shrink-0">
                                            <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                                                <DotSphereSpinner size={72} />
                                            </div>
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <PhaseSteps phases={phases} className="max-h-56 overflow-y-auto" />
                                            <p className="mt-2 text-xs text-muted-foreground">
                                                {t('objects.runbook.aiWorking', 'This takes several minutes — it reads the estate before it writes anything.')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* What we know about this object — shown, never written in.
                            Substituting values belongs to a RECOVERY runbook, where the values are
                            the ones the recovery actually used and the substitution is a record of
                            what happened. On the object screen the useful question is different:
                            does this runbook know enough about this object to be worth anything. */}
                        <div className="rounded-md border p-4 space-y-3">
                            <div className="flex items-center gap-2 font-medium">
                                <Wand2 className="h-4 w-4 text-blue-600" />
                                {t('objects.runbook.placeholders', 'What we know about this object')}
                            </div>
                            <p className="text-sm text-muted-foreground">
                                {t('objects.runbook.placeholdersHelp', 'The placeholders in this runbook and what they would be filled with, from this object and its latest backup. Nothing is changed here — the values go in when this becomes a recovery runbook, so that runbook records what the recovery actually used.')}
                            </p>

                            {placeholders === null ? (
                                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                            ) : !placeholders.hasMetadata ? (
                                <p className="text-sm text-muted-foreground">
                                    {t('objects.runbook.placeholdersNone', 'No backup metadata is held for this object yet, so nothing could be filled in. The runbook stays generic until it has been backed up.')}
                                </p>
                            ) : (
                                <>
                                    <div className="overflow-hidden rounded-md border">
                                        <table className="w-full text-sm">
                                            <tbody>
                                                {placeholders.filled.map((ph) => (
                                                    <tr key={ph.name} className="border-b last:border-b-0">
                                                        <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground">{'<' + ph.name + '>'}</td>
                                                        <td className="px-3 py-1.5">{ph.value}</td>
                                                        <td className="px-3 py-1.5 text-right text-xs text-muted-foreground">
                                                            {ph.usedInRunbook ? t('objects.runbook.placeholderUsed', 'used') : ''}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {placeholders.atRecovery.length > 0 && (
                                        <p className="text-xs text-muted-foreground">
                                            <span className="font-medium">{t('objects.runbook.placeholdersAtRecovery', 'Known only while a recovery is running')}:</span>{' '}
                                            {placeholders.atRecovery.map((n) => '<' + n + '>').join(', ')}
                                        </p>
                                    )}
                                    {placeholders.credentials.length > 0 && (
                                        <p className="text-xs text-muted-foreground">
                                            <span className="font-medium">{t('objects.runbook.placeholdersCreds', 'Supplied by the engineer at the time, never stored')}:</span>{' '}
                                            {placeholders.credentials.map((n) => '<' + n + '>').join(', ')}
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={!!busy}>
                        {t('common.close', 'Close')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
